import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000ComboBoxBase from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { type U2104AffiliationActionHandler } from "./U2104AffiliationActionHandlers";
import type { U2104LogicalUnit, U2104LogicalUnitAction } from "../../../../U2104Service";
import {
  u2104FlattenLogicalUnitCategoryGroups,
  u2104GroupLogicalUnitsByCategory,
} from "../../../../tools/u2104GroupLogicalUnitsByCategory";

interface U2104AffiliationActionDlgProps {
  u2104AffiliationActionHandler: U2104AffiliationActionHandler;
  t: TFunction;
  NS: string;
}

type U2104LogicalUnitComboItem = U2104LogicalUnit & { displayLabel: string };

function U2104AffiliationActionEditDlg({ u2104AffiliationActionHandler, t, NS }: U2104AffiliationActionDlgProps) {
  const { formStateHandlers } = u2104AffiliationActionHandler;
  const { i18n } = useTranslation();

  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      const originalOnBlur = formStateHandlers.onBlur;
      formStateHandlers.onBlur = () => {
        /* Ignore onBlur during cancel */
      };

      u2104AffiliationActionHandler.onDlgClose("cancel");

      setTimeout(() => {
        formStateHandlers.onBlur = originalOnBlur;
      }, 0);
    } else if (action === "save") {
      u2104AffiliationActionHandler.onDlgClose("save");
    }
  };

  const filteredLogicalUnitActions = useMemo(() => {
    const filtered = u2104AffiliationActionHandler.logicalUnits.filter(
      (lu) =>
        !u2104AffiliationActionHandler.gridLogicalUnitCodes.includes(lu.logicalUnitCode) ||
        (formStateHandlers.getData() && lu.logicalUnitCode === formStateHandlers.getData().logicalUnitCode),
    );
    const groups = u2104GroupLogicalUnitsByCategory(filtered, {
      noCategoryLabel: t("lblNoLogicalUnitCategory", { ns: NS }),
      locale: i18n.language,
    });
    return u2104FlattenLogicalUnitCategoryGroups(groups).map(
      (lu): U2104LogicalUnitComboItem => {
        const groupLabel =
          groups.find((g) => g.items.some((i) => i.id === lu.id))?.label ?? t("lblNoLogicalUnitCategory", { ns: NS });
        return {
          ...lu,
          displayLabel: `${groupLabel} — ${lu.logicalUnitDescription}`,
        };
      },
    );
  }, [
    u2104AffiliationActionHandler.logicalUnits,
    u2104AffiliationActionHandler.gridLogicalUnitCodes,
    formStateHandlers,
    t,
    NS,
    i18n.language,
  ]);

  return (
    <U2000SaveCancelDialog
      width="w-[700px]"
      height="h-[700px]"
      isOpen={u2104AffiliationActionHandler.isDialogOpen}
      onClose={handleClose}
      title={t("user.lblLogicalUnitAction", { ns: NS })}
      isValid={true}
    >
      <div className="flex flex-col gap-y-4">
        <U2000ComboBoxBase<U2104LogicalUnitComboItem>
          data={filteredLogicalUnitActions}
          keyField="id"
          descField="displayLabel"
          onChangeObject={u2104AffiliationActionHandler.onLogicalUnitChangeObject as (field: U2104LogicalUnitComboItem | null) => void}
          value={u2104AffiliationActionHandler.formStateHandlers.getData() as U2104LogicalUnitComboItem}
          disabled={u2104AffiliationActionHandler.formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
        />

        {u2104AffiliationActionHandler.logicalUnitActions &&
          u2104AffiliationActionHandler.logicalUnitActions.map((action: U2104LogicalUnitAction, index) => (
            <div key={index} className="mb-1 flex items-center gap-x-2">
              <div
                className={`h-6 w-6 cursor-pointer rounded-sm border-2 transition-colors duration-300 ${action.idChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`}
                onClick={() => u2104AffiliationActionHandler.onCheckLogicalUnitAction(action)}
              >
                {action.idChecked && (
                  <svg className="m-auto h-5 w-5 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <p className="inline-block text-gray-800">
                {action.logicalUnitActionCode} - {action.logicalUnitActionDescription}
              </p>
            </div>
          ))}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U2104AffiliationActionEditDlg;
